import {useRef} from 'react';

import {useMutation} from '@tanstack/react-query';

import {OperationStateTypesEnum, UserTypeEnum} from 'presentation/constants';
import {makeGetRequestPreviewProtocolPdf} from 'main/factories/usecases';
import {getUserInfoFromToken} from 'presentation/utils';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useRequestPreviewFlowStore} from 'presentation/pages/private/refund/pages/RequestPreview/store/useRequestPreviewFlowStore';

type DownloadPdfParams = {protocol: string; request: string};

type MutationVariables = DownloadPdfParams & {signal: AbortSignal};

function isAbortError(error: unknown): boolean {
	return error instanceof DOMException && error.name === 'AbortError';
}

export function useGetRequestPreviewProtocolPdf() {
	const abortControllerRef = useRef<AbortController | null>(null);
	const {beneficiary} = useAccountDataStore(store => store.accountData);
	const isHolder = beneficiary.tipoUsuarioC === UserTypeEnum.HOLDER;
	const userCode = getUserInfoFromToken().cdUsuario;
	const setIsPdfDownloading = useRequestPreviewFlowStore(state => state.setIsPdfDownloading);
	const setIsPdfExitWarningOpen = useRequestPreviewFlowStore(
		state => state.setIsPdfExitWarningOpen,
	);

	const mutation = useMutation({
		mutationFn: async ({protocol, request, signal}: MutationVariables) => {
			const response = await makeGetRequestPreviewProtocolPdf().get(
				{
					protocol,
					request,
					'patient-code': isHolder ? undefined : userCode,
				},
				signal,
			);
			return response;
		},
		onMutate: () => {
			setIsPdfDownloading(true);
		},
		onSuccess: response => {
			window.open(response.content.url, '_blank');
			setIsPdfDownloading(false);
			setIsPdfExitWarningOpen(false);
		},
		onError: () => {
			setIsPdfDownloading(false);
			setIsPdfExitWarningOpen(false);
		},
		onSettled: () => {
			setIsPdfDownloading(false);
		},
	});

	function downloadPdf({protocol, request}: DownloadPdfParams) {
		abortControllerRef.current = new AbortController();
		mutation.mutate({
			protocol,
			request,
			signal: abortControllerRef.current.signal,
		});
	}

	function cancelDownload() {
		abortControllerRef.current?.abort();
	}

	function setOperationState(state: OperationStateTypesEnum) {
		if (state === OperationStateTypesEnum.NONE) {
			mutation.reset();
		}
	}

	const DOWNLOAD_PDF_IS_LOADING = mutation.isPending;
	const DOWNLOAD_PDF_IS_ERROR =
		mutation.isError && !isAbortError(mutation.error);

	return {
		DOWNLOAD_PDF_IS_LOADING,
		DOWNLOAD_PDF_IS_ERROR,
		setOperationState,
		downloadPdf,
		cancelDownload,
	};
}
